import React, { useState, useEffect, memo, useCallback, useRef } from "react";
import { View, Text, Image, TouchableOpacity } from "react-native";
import { DEVICE_SIZES, useDeviceSize } from "rn-responsive-styles";
import { AppColors } from "../../utility/AppColors";
import { AppFontStyle } from "../../styles/AppFontStyle";
import Separator from "../Separator";
import { AppImages } from "../../utility/AppImages";
import { CommonButton } from "../../common/CommonButton";
import { useWsProvider } from "../../context/NativeApplicationContext";
import { toast } from "react-toastify";

interface KOBroadcasterComponentProps {
  handleConnectToBroadcaster: () => void;
  setupState: {
    isConnecting: boolean;
    isDownloading: boolean;
    downloadProgress: number;
    isDownloaded: boolean;
    estimatedDownloadTime: string;
    downloadSpeed: number;
    startTime: number | null;
  };
  checkDownloadStatus: () => boolean;
}

const getStatusDisplayText = (state?: string): string => {
  switch (state) {
    case "service-starting":
      return "Service Starting";
    case "idle":
      return "Ready";
    case "streaming-starting":
      return "Starting Stream";
    case "streaming":
      return "Streaming Live";
    case "network-disconnected":
      return "Network Disconnected";
    case "network-reconnecting":
      return "Reconnecting Network";
    case "restarting-streaming":
      return "Restarting Stream";
    case "service-error":
      return "Service Error";
    case "service-restarting":
      return "Restarting Service";
    case "streaming-error":
      return "Streaming Error";
    default:
      return state || "Ready";
  }
};

const KOBroadcasterComponent: React.FC<KOBroadcasterComponentProps> = ({
  handleConnectToBroadcaster,
  setupState,
  checkDownloadStatus,
}) => {
  const deviceSize = useDeviceSize();
  const [fetchingBroadcasterStatus, setFetchingBroadcasterStatus] =
    useState(false);
  const [hasTriedConnection, setHasTriedConnection] = useState(false);
  const [connectionFailed, setConnectionFailed] = useState(false);
  const isMountedRef = useRef(true);
  const timeoutRefs = useRef<NodeJS.Timeout[]>([]);

  const {
    nativeAppConnectionState,
    nativeAppStatus,
    isNativeAppConnected,
    shouldShowReconnect,
    smartReconnect,
    connectWithDeeplink,
  } = useWsProvider();

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      timeoutRefs.current.forEach(clearTimeout);
      timeoutRefs.current = [];
    };
  }, []);

  useEffect(() => {
    checkDownloadStatus();
  }, [checkDownloadStatus]);

  // Mark as tried when context has attempted connection
  useEffect(() => {
    if (nativeAppConnectionState !== "native-app-not-connected") {
      setHasTriedConnection(true);
    }
  }, [nativeAppConnectionState]);

  // Reset connection failed state when app is downloaded or connected
  useEffect(() => {
    if ((setupState.isDownloaded && connectionFailed) || isNativeAppConnected) {
      setConnectionFailed(false);
    }
  }, [setupState.isDownloaded, connectionFailed, isNativeAppConnected]);

  // Update connection failed state based on native app connection state
  useEffect(() => {
    if (
      nativeAppConnectionState === "native-app-not-connected" &&
      hasTriedConnection
    ) {
      setConnectionFailed(true);
    } else if (nativeAppConnectionState === "native-app-connected") {
      setConnectionFailed(false);
    }
  }, [nativeAppConnectionState, hasTriedConnection]);

  // Sync fetchingBroadcasterStatus with native app connection state
  useEffect(() => {
    if (nativeAppConnectionState === "native-app-connecting") {
      setFetchingBroadcasterStatus(true);
    } else {
      setFetchingBroadcasterStatus(false);
    }
  }, [nativeAppConnectionState]);

  const handleConnect = useCallback(async () => {
    if (!isMountedRef.current) return;

    setFetchingBroadcasterStatus(true);
    setHasTriedConnection(true);

    try {
      await connectWithDeeplink();
      if (isMountedRef.current) {
        setFetchingBroadcasterStatus(false);
        setConnectionFailed(false);
      }
    } catch (error: any) {
      if (isMountedRef.current) {
        setFetchingBroadcasterStatus(false);
        setConnectionFailed(true);
        toast.error(
          "KO Broadcaster not found. Please download and install it first."
        );
      }
    }
  }, [connectWithDeeplink]);

  return (
    <View style={{ justifyContent: "space-between", flexDirection: "row" }}>
      <View
        style={{
          backgroundColor: AppColors.purple_453B50,
          borderRadius: 10,
          padding:
            deviceSize === DEVICE_SIZES.XS || deviceSize === DEVICE_SIZES.SM
              ? 8
              : 24,
          width:
            deviceSize === DEVICE_SIZES.XS || deviceSize === DEVICE_SIZES.SM
              ? "100%"
              : "68%",
        }}
      >
        <View
          style={{
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 12,
          }}
        >
          <Text
            style={[
              deviceSize === DEVICE_SIZES.XS || deviceSize === DEVICE_SIZES.SM
                ? AppFontStyle.BOLD_ANYBODY_16
                : AppFontStyle.BOLD_ANYBODY_24,
              { color: AppColors.white },
            ]}
          >
            KO Broadcaster
          </Text>
          {!connectionFailed && (
            <CommonButton
              title={
                fetchingBroadcasterStatus ||
                nativeAppConnectionState === "native-app-connecting"
                  ? "Connecting..."
                  : isNativeAppConnected
                  ? "Connected"
                  : "Launch & Connect"
              }
              onTap={() => {
                if (
                  !isNativeAppConnected &&
                  !fetchingBroadcasterStatus &&
                  nativeAppConnectionState !== "native-app-connecting"
                ) {
                  handleConnect();
                }
              }}
              buttonStyle={{
                paddingVertical: 8,
                paddingHorizontal: 16,
                borderRadius: 8,
                shadowOpacity: 0,
                backgroundColor: isNativeAppConnected
                  ? AppColors.appGreenColor_51AF07
                  : fetchingBroadcasterStatus ||
                    nativeAppConnectionState === "native-app-connecting"
                  ? AppColors.gray_5C5C5C
                  : AppColors.appViolentColor_6D08C3,
                opacity:
                  fetchingBroadcasterStatus ||
                  nativeAppConnectionState === "native-app-connecting"
                    ? 0.7
                    : 1,
              }}
              fontStyle={[
                deviceSize === DEVICE_SIZES.XS || deviceSize === DEVICE_SIZES.SM
                  ? AppFontStyle.SEMIBOLD_ANYBODY_12
                  : AppFontStyle.SEMIBOLD_ANYBODY_14,
                { color: AppColors.white },
              ]}
              disabled={
                fetchingBroadcasterStatus ||
                nativeAppConnectionState === "native-app-connecting"
              }
            />
          )}
          {connectionFailed && setupState.isDownloaded && (
            <CommonButton
              title={isNativeAppConnected ? "Connected" : "Launch & Connect"}
              onTap={() => {
                if (!isNativeAppConnected && !fetchingBroadcasterStatus) {
                  handleConnect();
                }
              }}
              buttonStyle={{
                paddingVertical: 8,
                paddingHorizontal: 16,
                borderRadius: 8,
                shadowOpacity: 0,
                backgroundColor: isNativeAppConnected
                  ? AppColors.appGreenColor_51AF07
                  : AppColors.appViolentColor_6D08C3,
              }}
              fontStyle={[
                deviceSize === DEVICE_SIZES.XS || deviceSize === DEVICE_SIZES.SM
                  ? AppFontStyle.SEMIBOLD_ANYBODY_12
                  : AppFontStyle.SEMIBOLD_ANYBODY_14,
                { color: AppColors.white },
              ]}
              disabled={false}
            />
          )}
        </View>

        <Separator
          color={AppColors.black_2D2733}
          height={1}
          marginTop={0}
          marginBottom={12}
          marginHorizontal={0}
        />

        {/* Status Banner */}
        {fetchingBroadcasterStatus ||
        nativeAppConnectionState === "native-app-connecting" ? (
          <View
            style={{
              backgroundColor: "#FFA50033",
              borderRadius: 8,
              padding: 12,
              marginBottom: 16,
              flexDirection: "row",
              alignItems: "center",
              gap: 8,
              borderWidth: 1,
              borderColor: "#FFA500",
            }}
          >
            <Text
              style={[
                deviceSize === DEVICE_SIZES.XS || deviceSize === DEVICE_SIZES.SM
                  ? AppFontStyle.SEMIBOLD_ANYBODY_12
                  : AppFontStyle.SEMIBOLD_ANYBODY_14,
                { color: "#FFA500" },
              ]}
            >
              Checking KO Broadcaster connection...
            </Text>
          </View>
        ) : setupState.isDownloading ? (
          <View
            style={{
              backgroundColor: "#1E1B4B",
              borderRadius: 8,
              padding: 12,
              marginBottom: 16,
              borderWidth: 1,
              borderColor: AppColors.blue_233DFF,
            }}
          >
            <View
              style={{
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: 6,
              }}
            >
              <Text
                style={[
                  deviceSize === DEVICE_SIZES.XS || deviceSize === DEVICE_SIZES.SM
                    ? AppFontStyle.SEMIBOLD_ANYBODY_12
                    : AppFontStyle.SEMIBOLD_ANYBODY_14,
                  { color: AppColors.blue_233DFF },
                ]}
              >
                KO Broadcaster downloading
              </Text>
              <Text
                style={[
                  AppFontStyle.BOLD_ANYBODY_14,
                  { color: AppColors.blue_233DFF },
                ]}
              >
                {setupState.downloadProgress}%
              </Text>
            </View>
            <Text
              style={[
                AppFontStyle.REGULAR_ANYBODY_12,
                { color: AppColors.white },
              ]}
            >
              Estimated time remaining: {setupState.estimatedDownloadTime}
            </Text>
          </View>
        ) : (
          <View
            style={{
              backgroundColor: isNativeAppConnected ? "#86EFAC33" : "#C3081B33",
              borderRadius: 8,
              padding: 12,
              marginBottom: 16,
              flexDirection: "row",
              alignItems: "center",
              gap: 8,
              borderWidth: 1,
              borderColor: isNativeAppConnected ? "#86EFAC" : "#C3081B",
            }}
          >
            <Image
              source={
                isNativeAppConnected
                  ? AppImages.CheckInTick
                  : AppImages.CheckInTick
              }
              style={{ width: 20, height: 20 }}
              resizeMode="contain"
            />
            <View style={{ flex: 1 }}>
              <Text
                style={[
                  deviceSize === DEVICE_SIZES.XS ||
                  deviceSize === DEVICE_SIZES.SM
                    ? AppFontStyle.SEMIBOLD_ANYBODY_12
                    : AppFontStyle.SEMIBOLD_ANYBODY_14,
                  { color: isNativeAppConnected ? "#86EFAC" : "#C3081B" },
                ]}
              >
                {isNativeAppConnected
                  ? `KO Broadcaster is connected - ${getStatusDisplayText(
                      nativeAppStatus?.state
                    )}`
                  : "KO Broadcaster is disconnected"}
              </Text>
              {isNativeAppConnected && (
                <Text
                  style={[
                    AppFontStyle.REGULAR_ANYBODY_12,
                    { color: AppColors.white, marginTop: 4 },
                  ]}
                >
                  🎉 Awesome! We'll handle all your streaming automatically.
                </Text>
              )}
              {!isNativeAppConnected && hasTriedConnection && (
                <Text
                  style={[
                    AppFontStyle.REGULAR_ANYBODY_12,
                    { color: AppColors.white, marginTop: 4 },
                  ]}
                >
                  Please download and install KO Broadcaster, then click
                  Connect.
                </Text>
              )}
              {shouldShowReconnect && (
                <Text
                  style={[
                    AppFontStyle.REGULAR_ANYBODY_12,
                    { color: "#FFA500", marginTop: 4 },
                  ]}
                >
                  ⚠️ Service error detected. Use Smart Reconnect to fix.
                </Text>
              )}
            </View>
          </View>
        )}

        {/* Info Section */}
        <Text
          style={[
            deviceSize === DEVICE_SIZES.XS || deviceSize === DEVICE_SIZES.SM
              ? AppFontStyle.SEMIBOLD_ANYBODY_14
              : AppFontStyle.SEMIBOLD_ANYBODY_16,
            { color: AppColors.white, marginBottom: 8 },
          ]}
        >
          KO Broadcaster is our official streaming software
        </Text>

        <Text
          style={[
            AppFontStyle.REGULAR_ANYBODY_14,
            { color: AppColors.white, marginBottom: 4 },
          ]}
        >
          Estimated download time: 1-3 minutes (depending on your connection).
        </Text>

        <Text
          style={[
            AppFontStyle.REGULAR_ANYBODY_14,
            { color: AppColors.white, marginBottom: 16 },
          ]}
        >
          Download and install the KO Broadcaster, and we will handle all for
          you
        </Text>

        {shouldShowReconnect && (
          <CommonButton
            title="🔄 Smart Reconnect"
            onTap={smartReconnect}
            buttonStyle={{
              paddingVertical: 12,
              paddingHorizontal: 20,
              borderRadius: 8,
              shadowOpacity: 0,
              backgroundColor: "#FFA500",
              alignSelf: "flex-end",
              marginBottom: 16,
            }}
            fontStyle={[
              deviceSize === DEVICE_SIZES.XS || deviceSize === DEVICE_SIZES.SM
                ? AppFontStyle.SEMIBOLD_ANYBODY_12
                : AppFontStyle.SEMIBOLD_ANYBODY_14,
              { color: AppColors.black_18151b },
            ]}
          />
        )}

        {
          <CommonButton
            title={
              setupState.isConnecting
                ? `Downloading... ${setupState.downloadProgress || 0}% (${setupState.estimatedDownloadTime})`
                : "Download KO Broadcaster"
            }
            onTap={() => {
              if (!setupState.isConnecting) {
                handleConnectToBroadcaster();
              }
            }}
            buttonStyle={{
              paddingVertical: 12,
              paddingHorizontal: 20,
              borderRadius: 8,
              shadowOpacity: 0,
              backgroundColor: AppColors.appViolentColor_6D08C3,
              opacity: setupState.isConnecting ? 0.7 : 1,
              alignSelf: "flex-end",
              marginTop: 16,
            }}
            fontStyle={[
              deviceSize === DEVICE_SIZES.XS || deviceSize === DEVICE_SIZES.SM
                ? AppFontStyle.SEMIBOLD_ANYBODY_12
                : AppFontStyle.SEMIBOLD_ANYBODY_14,
              { color: AppColors.white },
            ]}
            disabled={setupState.isConnecting}
          />
        }
      </View>
    </View>
  );
};

export default memo(KOBroadcasterComponent);
